/**
 * Competition Submissions Page
 *
 * Displays all submissions for a competition and keeps current user's
 * submissions pinned to the top.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppNavbar from '../components/AppNavbar';
import { useAuth } from '../context/AuthContext';
import competitionService from '../services/competitionService';
import './CompetitionSubmissionsPage.css';

const CompetitionSubmissionsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  const [competition, setCompetition] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [revalidatingSubmissionId, setRevalidatingSubmissionId] = useState(null);
  const [revalidationMessage, setRevalidationMessage] = useState('');
  const [visibilityType, setVisibilityType] = useState('public');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [competitionData, submissionsData] = await Promise.all([
        competitionService.getById(id),
        competitionService.getSubmissions(id)
      ]);

      const competitionRecord = competitionData.competition || competitionData;
      const submissionsList = Array.isArray(submissionsData.submissions) ? submissionsData.submissions : [];

      setCompetition(competitionRecord);
      setVisibilityType(submissionsData.visibilityType || competitionRecord.visibilityMode || 'public');
      setSubmissions(sortSubmissionsWithUserFirst(submissionsList, user?.id, competitionRecord?.status));
    } catch (err) {
      console.error('Failed to load submissions page:', err);
      setError('Failed to load submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRevalidateSubmission = async (submissionId) => {
    try {
      setRevalidatingSubmissionId(submissionId);
      setRevalidationMessage('');
      const response = await competitionService.revalidateSubmission(submissionId);
      const responseStatus = response?.submission?.status;
      if (responseStatus === 'valid') {
        setRevalidationMessage('Submission is now valid. Great job fixing it.');
      } else {
        setRevalidationMessage('Submission checked again. Please review the remaining issues and fix them.');
      }
      await loadData();
    } catch (revalidateError) {
      const apiMessage = revalidateError?.response?.data?.message;
      setRevalidationMessage(apiMessage || 'Could not revalidate this submission right now.');
    } finally {
      setRevalidatingSubmissionId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const navigationItems = isAuthenticated
    ? [
        { label: 'Home', to: '/', isActive: false },
        { label: 'Back to Competitions', to: '/competitions', isActive: false },
        { label: 'Competition Details', to: `/competitions/${id}`, isActive: false },
        { label: 'Dashboard', to: '/dashboard', isActive: false }
      ]
    : [
        { label: 'Home', to: '/', isActive: false },
        { label: 'Back to Competitions', to: '/competitions', isActive: false },
        { label: 'Competition Details', to: `/competitions/${id}`, isActive: false },
        { label: 'Login', to: '/login', isActive: false }
      ];

  if (loading) {
    return (
      <div className="competition-submissions-page">
        <div className="submissions-loading-state">
          <div className="submissions-spinner"></div>
          <p>Loading submissions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="competition-submissions-page">
        <div className="submissions-error-state">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="submissions-error-actions">
            <button type="button" className="submissions-nav-button" onClick={loadData}>
              Retry
            </button>
            <button type="button" className="submissions-nav-button" onClick={() => navigate(`/competitions/${id}`)}>
              Back to Competition
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="competition-submissions-page">
      <AppNavbar
        user={user}
        onLogout={isAuthenticated ? handleLogout : undefined}
        navigationItems={navigationItems}
        brandTo="/"
        showUserSection={isAuthenticated}
      />

      <div className="submissions-content">
        <header className="submissions-header">
          <div>
            <h1>{competition?.name || 'Competition'} Submissions</h1>
            <p>
              Total submissions: <strong>{submissions.length}</strong>
              {user?.id ? ' (your submissions are shown first)' : ''}
            </p>
            {competition?.status !== 'completed' && (
              <p className="score-hidden-note">Scores will be visible after the competition is completed.</p>
            )}
            {revalidationMessage && (
              <p className="revalidation-message">{revalidationMessage}</p>
            )}
          </div>
        </header>

        {submissions.length === 0 ? (
          <div className="submissions-empty-state">
            <h3>No submissions yet</h3>
            {competition?.status === 'active' && visibilityType === 'hidden' ? (
              <p>No submissions from your account yet. Other creators' submissions are hidden during active phase.</p>
            ) : (
              <p>Submissions will appear here once participants upload entries.</p>
            )}
          </div>
        ) : (
          <div className="submissions-list">
            {submissions.map((submission, index) => {
              const submissionUserId = getSubmissionUserId(submission);
              const isCurrentUsers = !!user?.id && submissionUserId === user.id;

              return (
                <article
                  key={submission.id || `${submission.videoUrl}-${index}`}
                  className={`submission-card ${isCurrentUsers ? 'submission-card-own' : ''}`}
                >
                  <div className="submission-card-top">
                    <div className="submission-meta">
                      <span className="submission-number">Submission #{submission.submissionNumber || index + 1}</span>
                      {isCurrentUsers && <span className="submission-own-badge">Your Submission</span>}
                      <span className={`submission-status status-${submission.status || 'pending'}`}>
                        {submission.status || 'pending'}
                      </span>
                    </div>
                    {competition?.status === 'completed' ? (
                      <div className="submission-score">
                        Score: <strong>{formatScore(submission.score)}</strong>
                      </div>
                    ) : (
                      <div className="submission-score pending">Score hidden</div>
                    )}
                  </div>

                  <div className="submission-user-line">
                    By {getSubmissionUserLabel(submission)}
                  </div>

                  <a
                    href={submission.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="submission-link"
                  >
                    Open Submission Link
                  </a>

                  <div className="submission-details">
                    <span>Type: {submission.submissionType || 'unknown'}</span>
                    <span>Submitted: {formatDate(submission.submittedAt)}</span>
                  </div>

                  {isCurrentUsers && submission.status === 'invalid' && (
                    <div className="submission-invalid-box">
                      <h4>Why your submission is invalid</h4>
                      <ul>
                        {extractValidationMessages(submission.validationErrors).map((message, messageIndex) => (
                          <li key={`${submission.id}-validation-${messageIndex}`}>{message}</li>
                        ))}
                      </ul>

                      <div className="submission-fix-actions">
                        <button
                          type="button"
                          className="submission-fix-button"
                          onClick={() => handleRevalidateSubmission(submission.id)}
                          disabled={revalidatingSubmissionId === submission.id}
                        >
                          {revalidatingSubmissionId === submission.id ? 'Revalidating...' : 'I Fixed It, Revalidate'}
                        </button>
                        <button
                          type="button"
                          className="submission-fix-button secondary"
                          onClick={() => navigate(`/competitions/${id}?openSubmit=1`)}
                        >
                          Submit Fixed Entry
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

function sortSubmissionsWithUserFirst(submissions, currentUserId, competitionStatus) {
  return [...submissions].sort((a, b) => {
    const aUserId = getSubmissionUserId(a);
    const bUserId = getSubmissionUserId(b);
    const aIsMine = !!currentUserId && aUserId === currentUserId;
    const bIsMine = !!currentUserId && bUserId === currentUserId;

    if (aIsMine && !bIsMine) {
      return -1;
    }

    if (!aIsMine && bIsMine) {
      return 1;
    }

    if (competitionStatus === 'completed') {
      const aScore = typeof a.score === 'number' ? a.score : Number(a.score || 0);
      const bScore = typeof b.score === 'number' ? b.score : Number(b.score || 0);
      if (aScore !== bScore) {
        return bScore - aScore;
      }
    }

    const aSubmittedAt = Number(a.submittedAt || 0);
    const bSubmittedAt = Number(b.submittedAt || 0);
    return bSubmittedAt - aSubmittedAt;
  });
}

function extractValidationMessages(validationErrors) {
  if (!Array.isArray(validationErrors) || validationErrors.length === 0) {
    return ['Validation failed. Please check your video link, privacy, and required hashtag.'];
  }

  return validationErrors.map(function(error) {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      return error.details || error.message || 'Validation error';
    }

    return 'Validation error';
  });
}

function getSubmissionUserId(submission) {
  if (!submission) {
    return null;
  }

  if (typeof submission.userId === 'string') {
    return submission.userId;
  }

  if (submission.userId && typeof submission.userId === 'object') {
    return submission.userId.id || null;
  }

  return null;
}

function getSubmissionUserLabel(submission) {
  if (!submission || !submission.userId) {
    return 'Participant';
  }

  if (typeof submission.userId === 'string') {
    return 'Participant';
  }

  return submission.userId.username || submission.userId.name || submission.userId.email || 'Participant';
}

function formatScore(score) {
  const numericScore = typeof score === 'number' ? score : Number(score);
  if (Number.isNaN(numericScore)) {
    return '0.00';
  }

  return numericScore.toFixed(2);
}

function formatDate(value) {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default CompetitionSubmissionsPage;
